/**
 * Clause-specific Initial Findings Assessment buttons exposed by the CER
 * Section 5 portal. Transcribed from the portal audit supplied 2026-08-04.
 *
 * Keep this list explicit: the portal varies its buttons per clause and broad
 * prefix-based defaults allow inspectors to record values the portal rejects.
 */
const groups = [
    [['compliant', 'not_cited', 'medium_non_compliance'], ['Documentation 1']],
    [
        ['compliant', 'medium_non_compliance', 'rectification_work'],
        [
            'SB & Labelling 13',
            'Pre-assembled BS 63',
            'SB & Labelling 11',
            'Pre-assembled BS 51',
            'SB & Labelling 19',
        ],
    ],
    [
        ['compliant', 'rectification_work'],
        [
            'SB & Labelling 14',
            'SB & Labelling 22',
            'Pre-assembled BS 69',
            'Pre-assembled BS 72',
            'Pre-assembled BS 92',
            'Pre-assembled BS 93',
            'Pre-assembled BS 50',
            'Pre-assembled BS 58',
            'SB & Labelling 16',
            'Pre-assembled BS 12',
            'Pre-assembled BS 23',
            'SB & Labelling 10',
            'SB & Labelling 29',
            'SB & Labelling 30',
            'SB & Labelling 31',
            'Pre-assembled BS 43',
            'Pre-assembled BS 73',
            'Pre-assembled BS 75',
            'Pre-assembled BS 27',
            'Pre-assembled BS 28',
            'Pre-assembled BS 31',
        ],
    ],
    [
        ['compliant', 'unsafe'],
        [
            'SB & Labelling 15',
            'Pre-assembled BS 91',
            'Pre-assembled BS 95',
            'Pre-assembled BS 97',
            'Pre-assembled BS 5',
            'SB & Labelling 33',
        ],
    ],
    [
        ['compliant', 'medium_non_compliance'],
        [
            'SB & Labelling 21',
            'Pre-assembled BS 71',
            'Pre-assembled BS 76',
            'Pre-assembled BS 22',
            'Pre-assembled BS 25',
            'Pre-assembled BS 84',
            'Pre-assembled BS 85',
            'SB & Labelling 23',
            'SB & Labelling 24',
            'SB & Labelling 1',
            'SB & Labelling 2',
            'SB & Labelling 6',
            'SB & Labelling 8',
            'SB & Labelling 17',
            'SB & Labelling 25',
            'SB & Labelling 28',
            'Pre-assembled BS 15',
            'Pre-assembled BS 16',
            'Pre-assembled BS 20',
            'Pre-assembled BS 98',
            'SB & Labelling 20',
            'SB & Labelling 26',
            'Pre-assembled BS 36',
            'Pre-assembled BS 29',
            'Pre-assembled BS 32',
            'Pre-assembled BS 33',
            'Pre-assembled BS 34',
        ],
    ],
    [
        ['compliant', 'not_applicable', 'rectification_work'],
        [
            'Pre-assembled BS 42',
            'Pre-assembled BS 64',
            'Pre-assembled BS 67',
            'Pre-assembled BS 68',
            'Pre-assembled BS 78',
            'Pre-assembled BS 79',
            'Pre-assembled BS 80',
            'Pre-assembled BS 81',
            'Pre-assembled BS 82',
            'Pre-assembled BS 83',
            'Pre-assembled BS 90',
            'Pre-assembled BS 52',
            'Pre-assembled BS 53',
            'Pre-assembled BS 54',
            'Pre-assembled BS 86',
            'Pre-assembled BS 87',
            'Pre-assembled BS 44',
            'Pre-assembled BS 45',
            'Pre-assembled BS 46',
            'Pre-assembled BS 47',
            'Pre-assembled BS 48',
            // 'Pre-assembled BS 49' was here until a live DOM check on 2026-08-09
            // (portal build 3.9.54.0) found the portal offers it
            // Compliant / Not Applicable / Medium non-compliance — see below.
            'Pre-assembled BS 26',
            'Pre-assembled BS 30',
        ],
    ],
    [
        ['compliant', 'not_applicable', 'medium_non_compliance', 'rectification_work', 'unsafe'],
        ['Pre-assembled BS 65'],
    ],
    [['compliant', 'not_applicable', 'rectification_work', 'unsafe'], ['Pre-assembled BS 66']],
    [
        ['compliant', 'not_applicable', 'medium_non_compliance'],
        [
            'Pre-assembled BS 70',
            'Pre-assembled BS 74',
            'Pre-assembled BS 77',
            'Pre-assembled BS 24',
            'Pre-assembled BS 38',
            'Pre-assembled BS 39',
            'Pre-assembled BS 40',
            'Pre-assembled BS 41',
            'Pre-assembled BS 88',
            'Pre-assembled BS 89',
            'SB & Labelling 27',
            'Pre-assembled BS 55',
            'Pre-assembled BS 56',
            'Pre-assembled BS 57',
            'Pre-assembled BS 59',
            'Pre-assembled BS 60',
            'Pre-assembled BS 61',
            'Pre-assembled BS 62',
            'SB & Labelling 3',
            'SB & Labelling 4',
            'SB & Labelling 18',
            'Pre-assembled BS 18',
            'Pre-assembled BS 19',
            'Pre-assembled BS 21',
            'Pre-assembled BS 37',
            // Moved here from the `rectification_work` group after a live DOM check
            // on 2026-08-09 (portal build 3.9.54.0, submission 76663). The portal
            // offers Medium non-compliance, not Rectification work required. This
            // was the ONLY discrepancy across all 119 Section 5 clauses versus the
            // 2026-08-04 audit — whether the portal changed or the audit mis-transcribed
            // is unknown, but the live DOM wins.
            'Pre-assembled BS 49',
        ],
    ],
    [['compliant', 'medium_non_compliance', 'rectification_work', 'unsafe'], ['Pre-assembled BS 94']],
    [
        ['compliant', 'rectification_work', 'unsafe'],
        ['Pre-assembled BS 96', 'Pre-assembled BS 4'],
    ],
    [['compliant', 'not_applicable'], ['Pre-assembled BS 99']],
    [
        ['compliant', 'not_applicable', 'medium_non_compliance', 'rectification_work'],
        ['Pre-assembled BS 17', 'Pre-assembled BS 35'],
    ],
    [
        ['compliant', 'not_applicable', 'minor_non_compliance', 'medium_non_compliance'],
        ['SB & Labelling 5'],
    ],
    [['not_applicable', 'unsafe_product_recall'], ['Pre-assembled BS 13']],
];
export const SECTION5_PORTAL_ASSESSMENT_VALUES = buildMap(groups);
function buildMap(optionGroups) {
    const result = {};
    for (const [values, clauseIds] of optionGroups) {
        for (const clauseId of clauseIds) {
            if (result[clauseId])
                throw new Error(`Duplicate Section 5 portal clause: ${clauseId}`);
            result[clauseId] = values;
        }
    }
    return Object.freeze(result);
}
//# sourceMappingURL=section5-portal-options.js.map